import { bookingSessionKeys, flightSearchQueryKeys } from '@massilia/queries'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useCallback } from 'react'

import { useFlightSearchStore } from '@/stores/flight-search.store'

/**
 * Paramètre d'URL qui force une recherche neuve.
 *
 * Il joue trois rôles d'un coup, et c'est pour ça qu'il transite par l'URL plutôt que
 * par un état React (qui ne survivrait pas au rechargement) :
 *   1. l'URL diffère de la précédente, donc un `reloadDocument` vers la même page ne
 *      peut pas être avalé comme une navigation nulle ;
 *   2. il entre dans la queryKey react-query, ce qui écarte la réponse en cache ;
 *   3. il est relayé au serveur en `refresh: true`, qui contourne alors son propre
 *      cache de recherche (120 s) — sans quoi il rendrait le même `transactionKey`,
 *      donc les mêmes offres dont le cache prix vient d'expirer.
 */
const REFRESH_SEARCH_PARAM = 'refresh'

interface RestartFlightSearchOptions {
  /** Params de la page résultats à rejouer. Défaut : ceux de l'URL, sinon la dernière recherche mémorisée. */
  search?: Record<string, unknown>
  /** Route résultats à rejouer. Défaut : la page courante si c'en est une, sinon la dernière mémorisée. */
  to?: string
}

/**
 * « Relancer la recherche » après une session expirée.
 *
 * Le cache d'offres Affidium tient 20 min alors que la BookingSession tient 12 h : passé
 * ce délai, tout ce qui porte l'ancien `transactionKey` est mort. Rejouer la page sans
 * rien purger ramenait l'utilisateur sur exactement la même impasse — le bouton semblait
 * ne rien faire. On purge donc les trois caches clients, puis on repart en navigation
 * document (état React remis à zéro) avec le marqueur de rafraîchissement.
 */
export function useRestartFlightSearch() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useRouterState({ select: (state) => state.location })
  const lastFlightResultsParams = useFlightSearchStore((state) => state.lastFlightResultsParams)
  const lastFlightResultsRoute = useFlightSearchStore((state) => state.lastFlightResultsRoute)

  return useCallback(
    (options: RestartFlightSearchOptions = {}) => {
      queryClient.removeQueries({ queryKey: flightSearchQueryKeys.search })
      queryClient.removeQueries({ queryKey: flightSearchQueryKeys.multiSearch })
      queryClient.removeQueries({ queryKey: flightSearchQueryKeys.priceOffer })
      queryClient.removeQueries({ queryKey: bookingSessionKeys.all })

      // Les critères de la page courante priment : ce sont ceux que l'utilisateur a
      // sous les yeux. Le store ne sert que depuis les étapes du tunnel, où l'URL ne
      // porte plus que l'identifiant de session. Route et critères se choisissent
      // ensemble — rejouer les params d'une page sur l'autre ne donnerait rien.
      const currentSearch = location.search as Record<string, unknown> | undefined
      const target = options.search
        ? { search: options.search, to: options.to }
        : currentSearch?.flightSearch
          ? { search: currentSearch, to: options.to ?? location.pathname }
          : lastFlightResultsParams
            ? { search: lastFlightResultsParams, to: options.to ?? lastFlightResultsRoute }
            : null

      // Sans critères de recherche sous la main, on ne peut pas rejouer les résultats :
      // on renvoie l'utilisateur saisir sa recherche plutôt que sur une page vide.
      if (!target?.search.flightSearch) {
        void navigate({ reloadDocument: true, to: '/recherche' })
        return
      }

      void navigate({
        reloadDocument: true,
        search: { ...target.search, [REFRESH_SEARCH_PARAM]: Date.now() },
        // `to` élargi en `string` : la route de destination n'est connue qu'à
        // l'exécution (page courante, page mémorisée ou défaut), donc l'inférence des
        // params de recherche par route ne s'applique pas ici. Même parti pris que
        // le retour « Étape précédente » de la page informations.
        to: (target.to ?? '/vols/resultats') as string,
      })
    },
    [
      lastFlightResultsParams,
      lastFlightResultsRoute,
      location.pathname,
      location.search,
      navigate,
      queryClient,
    ]
  )
}
